import { useTranslation } from 'react-i18next';
import { File } from '@phosphor-icons/react';
import type { FilteredDocumentDto } from '@monorepo/shared';

interface FilteredDocumentsTableProps {
  documents: FilteredDocumentDto[];
  isLoading: boolean;
  filter: string;
  onOpenDocument: (nodeId: string) => void;
  onResendDocument: (documentId: string) => void;
}

const formatOptionalDate = (date: Date | null | undefined) =>
  date ? new Date(date).toLocaleDateString('fr-FR') : '—';

type MetricColumn = {
  labelKey: string;
  render: (doc: FilteredDocumentDto) => string;
};

const getMetricColumn = (filter: string): MetricColumn => {
  if (filter === 'diffused') {
    return {
      labelKey: 'filtered_documents.columns.diffusion_date',
      render: (doc) => formatOptionalDate(doc.lastDiffusedAt),
    };
  }

  if (filter === 'validated') {
    return {
      labelKey: 'filtered_documents.columns.validation_date',
      render: (doc) => formatOptionalDate(doc.validatedAt),
    };
  }

  return {
    labelKey: 'filtered_documents.columns.opened',
    render: (doc) => `${doc.totalOpened}/${doc.totalRecipients}`,
  };
};

export const FilteredDocumentsTable = ({
  documents,
  isLoading,
  filter,
  onOpenDocument,
  onResendDocument,
}: FilteredDocumentsTableProps) => {
  const { t } = useTranslation();
  const showResend = filter === 'pending' || filter === 'not-consulted';
  const metricColumn = getMetricColumn(filter);

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="p-4 space-y-3">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="h-12 bg-gray-100 rounded animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  if (documents.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-8 text-center text-gray-500">
        {t('filtered_documents.empty')}
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto">
      <table className="w-full min-w-[720px]">
        <thead className="bg-gray-50 border-b border-gray-200">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              {t('filtered_documents.columns.name')}
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              {t('filtered_documents.columns.theme')}
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              {t('filtered_documents.columns.folder')}
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              {t(metricColumn.labelKey)}
            </th>
            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              {t('filtered_documents.columns.actions')}
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {documents.map((doc) => (
            <tr key={doc.id} className="hover:bg-gray-50">
              <td className="px-4 py-3">
                <div className="flex items-center gap-2">
                  <File size={20} weight="regular" className="text-gray-400" />
                  <span className="text-sm font-medium text-gray-900">{doc.name}</span>
                </div>
              </td>
              <td className="px-4 py-3">
                {doc.themeName && (
                  <span className="px-2 py-1 text-xs font-medium rounded-md bg-gray-100 text-gray-700">
                    {doc.themeName}
                  </span>
                )}
              </td>
              <td className="px-4 py-3">
                <span className="text-sm text-gray-600">{doc.folderName || '-'}</span>
              </td>
              <td className="px-4 py-3">
                <span className="text-sm text-gray-600">{metricColumn.render(doc)}</span>
              </td>
              <td className="px-4 py-3 text-right">
                <div className="flex items-center justify-end gap-2">
                  <button
                    onClick={() => onOpenDocument(doc.nodeId)}
                    className="px-3 py-1.5 text-sm font-medium text-white bg-primary rounded-md hover:bg-opacity-90 transition"
                  >
                    {t('filtered_documents.actions.open')}
                  </button>
                  {showResend && (
                    <button
                      onClick={() => onResendDocument(doc.id)}
                      className="px-3 py-1.5 text-sm font-medium text-primary border border-primary rounded-md hover:bg-primary hover:text-white transition"
                    >
                      {t('filtered_documents.actions.resend')}
                    </button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
